import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Trash2, Ghost } from "lucide-react";
import { fetchGallery, deleteArtwork } from "@/lib/api";
import { getPage } from "@/data/registry";
import { sfx } from "@/lib/sounds";

export default function GalleryWall() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetchGallery()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const remove = async (id) => {
    sfx.pop();
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await deleteArtwork(id);
    } catch (e) {}
  };

  const download = (item) => {
    sfx.click();
    const a = document.createElement("a");
    a.href = item.image;
    a.download = `${item.title.replace(/\s+/g, "-").toLowerCase()}.jpg`;
    a.click();
  };

  return (
    <section id="gallery" data-testid="gallery-section" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        className="font-display text-5xl text-paper sm:text-6xl"
      >
        The Haunted <span className="text-slime text-glow-slime">Gallery</span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        className="mt-3 max-w-xl text-paper/65"
      >
        Saved masterpieces hang here in glowing frames. Finish a page in the
        studio and hit Save to hang yours.
      </motion.p>

      {items === null ? (
        <div className="mt-12 h-40 animate-pulse rounded-3xl bg-white/5" />
      ) : items.length === 0 ? (
        <div className="card-edge mt-12 flex flex-col items-center gap-3 px-6 py-14 text-center">
          <span className="animate-floaty text-pumpkin">
            <Ghost size={44} />
          </span>
          <p className="font-hand text-2xl text-paper/80">No masterpieces yet!</p>
          <p className="max-w-sm text-sm text-paper/50">
            Color a page and press Save — it will magically appear right here.
          </p>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              data-testid={`gallery-card-${item.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: (i % 4) * 0.07 }}
              whileHover={{ y: -6, rotate: i % 2 ? 0.8 : -0.8 }}
              className="group relative overflow-hidden rounded-2xl border-2 border-pumpkin/40 bg-night p-3 shadow-frame transition-shadow hover:shadow-glow-orange"
            >
              <div className="overflow-hidden rounded-xl bg-[#FFFDF8]">
                <img src={item.image} alt={item.title} className="w-full" />
              </div>
              <div className="mt-3 flex items-center justify-between px-1 pb-1">
                <div>
                  <div className="text-sm font-semibold text-paper">{item.title}</div>
                  <div className="text-[11px] text-paper/50">
                    {getPage(item.page_id)?.title || "Scribble"}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    data-testid={`gallery-download-${item.id}`}
                    onClick={() => download(item)}
                    aria-label="Download artwork"
                    className="rounded-full border border-white/15 p-2 text-paper/70 transition-colors hover:border-slime hover:text-slime"
                  >
                    <Download size={14} />
                  </button>
                  <button
                    data-testid={`gallery-delete-${item.id}`}
                    onClick={() => remove(item.id)}
                    aria-label="Remove artwork"
                    className="rounded-full border border-white/15 p-2 text-paper/70 transition-colors hover:border-candy hover:text-candy"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
